import {
  PathPickHotDealLineSheet,
  PathPickMarketPriceBasis,
  PathPickPopularProduct,
  PathPickProductCardViewSettings,
  PathPickProductRequestFilterKind,
  PathPickProductRequestKind,
  PathPickProductRequestStatusFilter,
  PopularProductSeed,
} from "@/features/pathpick/types";
import { PathPickProductRequestParams } from "@/features/pathpick/types/request-params";
import { createPopularProduct } from "@/features/pathpick/utils";

// 비교 기준 → 상품 조회의 market_price_basis 파라미터. 크림·포이즌 양쪽에 같이 적용된다.
// 상품 찾기와 담은 상품이 같은 매핑을 쓰므로 두 feature의 상위인 여기에 둔다.
export const PATH_PICK_MARKET_PRICE_BASIS_PARAMS: Record<
  PathPickMarketPriceBasis,
  NonNullable<PathPickProductRequestParams["marketPriceBasis"]>
> = {
  bidPrice: "lowest_price",
  tradePrice: "last_price",
};

// 상품 카드 보기 설정 — 상품 찾기와 담은 상품이 이 키 하나를 공유한다.
export const PATH_PICK_PRODUCT_CARD_VIEW_STORAGE_KEY =
  "pathpick:product-card-view";

// 기본값: 비교 표시 켜고 위아래로 보기. 저장값이 없거나 알 수 없는 값이면 여기로 떨어진다.
export const DEFAULT_PATH_PICK_PRODUCT_CARD_VIEW: PathPickProductCardViewSettings =
  {
    showComparison: true,
    comparisonLayout: "stack",
  };

// 하단 플로팅 선택 바(상품 찾기·담은 상품) — 목록 페이지네이션이 화면 하단에 sticky로 붙어 있어
// (PC 한 줄 약 56px, 좁은 폭 두 줄 약 96px) 그 바로 위에 띄운다.
// PWA 설치 안내 배너가 떠 있으면 그만큼 더 올린다(없으면 변수가 0px).
export const PATH_PICK_SELECTION_BAR_BOTTOM = {
  base: "calc(98px + var(--pwa-banner-height, 0px))",
  md: "calc(88px + var(--pwa-banner-height, 0px))",
};

// 플로팅 선택 바의 z-index. 목록 카드의 sticky footer(페이지네이션)는 이보다 위에 둬야
// footer 안의 페이지 크기 드롭다운이 바에 가리지 않는다(PathPickSectionCard 참고).
export const PATH_PICK_SELECTION_BAR_Z_INDEX = 20;

// 플로팅 선택 바의 실측 높이를 싣는 CSS 변수 — 바는 fixed라 목록 흐름에서 자리를 차지하지 않아,
// 목록 끝의 여백(PathPickSelectionBarSpacer)이 이 값만큼 비켜 마지막 카드가 바에 가리지 않게 한다.
// 한 화면에는 바가 하나뿐이라 두 화면이 변수 하나를 쓴다. 바가 없으면(선택 0개) 변수를 지워 여백도 0이 된다.
export const PATH_PICK_SELECTION_BAR_HEIGHT_VAR =
  "--pathpick-selection-bar-height";

// 신청 이력 있음 = 4종 중 하나라도 신청 — request_type__in 합집합으로 표현한다.
// 주문에서만 접수하는 KREAM_EXCEL_CONFIRMED_QUANTITY도 넣어야 한다 — 서버의 "NONE"이
// 4종 모두 이력 없음을 뜻하므로, 빼 두면 확정 수량만 신청한 상품이 `작업 전`(NONE)에서도
// `등록 신청함`에서도 빠져 어느 탭에도 나오지 않는다.
export const PATH_PICK_ALL_REQUEST_KINDS: PathPickProductRequestKind[] = [
  "NAVER_SYNC",
  "NAVER_EXCEL",
  "KREAM_EXCEL",
  "KREAM_EXCEL_CONFIRMED_QUANTITY",
];

// 등록 신청함(신청 이력 있음) 기준 종류 — 네이버 외 마켓 연동 권한이 있는 바이어는 그 마켓 연동만 한 상품도
// 이 탭에 나와야 해서 그 마켓 연동을 더한다. 네이버만이면 요청 파라미터를 기존 그대로 둔다.
// syncKinds — usePathPickSyncKinds()(권한 있는 마켓, 네이버 포함).
export const getPathPickAllRequestKinds = (
  syncKinds: PathPickProductRequestKind[]
): PathPickProductRequestKind[] => {
  const extraKinds = syncKinds.filter((kind) => kind !== "NAVER_SYNC");
  return extraKinds.length
    ? [...PATH_PICK_ALL_REQUEST_KINDS, ...extraKinds]
    : PATH_PICK_ALL_REQUEST_KINDS;
};

// 신청 이력 없음 — 서버가 request_type__in의 "NONE"을 지원하므로 ["NONE"]으로 표현할 수 있다.
// 담은 상품 작업 전 탭은 이미 ["NONE"]을 직접 보내고 있고, 이 상수는 상품 찾기의
// "신청 이력 있는 상품 제외" 토글 전용으로 남아 있다. undefined인 동안은 파라미터가
// 직렬화에서 빠져 "신청 이력 무관"으로 조회되므로 그 토글은 동작하지 않는다.
// TODO: 상품 찾기 제외 토글을 검증한 뒤 ["NONE"]으로 교체한다.
export const PATH_PICK_NO_REQUEST_KINDS:
  | PathPickProductRequestFilterKind[]
  | undefined = undefined;

// 사용량 경고 임계값(%) — 이 비율 이상 소진되면 경고 배지와 경고색 프로그레스바로 바뀐다.
export const PATHPICK_USAGE_WARNING_THRESHOLD = 80;

// ===== 인기 상품과 브랜드 섹션 목데이터 (TODO: API 연결 시 교체) =====

const POPULAR_PRODUCT_SEEDS: PopularProductSeed[] = [
  {
    id: 1001,
    brandName: "Prada",
    name: "Knitwear FW25",
    sku: "875161XKFP108",
    categories: ["의류", "Knitwear"],
    season: "FW25",
    color: "Green",
    price: 2424000,
    kreamPrice: 3433000,
    kreamBidCount: 210,
    expectedMargin: 1009000,
    marginRate: 29.4,
    sizes: [
      { name: "S", stock: 4 },
      { name: "M", stock: 6 },
      { name: "L", stock: 4 },
    ],
  },
  {
    id: 1002,
    brandName: "Gucci",
    name: "Sandals SS25",
    sku: "1I308OF075047",
    categories: ["신발", "Sandals"],
    season: "SS25",
    color: "Black",
    price: 808000,
    kreamPrice: 1092000,
    kreamBidCount: 194,
    expectedMargin: 284000,
    marginRate: 26.0,
    sizes: [
      { name: "240", stock: 5 },
      { name: "250", stock: 6 },
    ],
  },
  {
    id: 1003,
    brandName: "Gucci",
    name: "Sandals SS25",
    sku: "1I308OF075167",
    categories: ["신발", "Sandals"],
    season: "SS25",
    color: "Black",
    price: 874000,
    kreamPrice: 1181000,
    kreamBidCount: 188,
    expectedMargin: 307000,
    marginRate: 26.0,
    sizes: [
      { name: "240", stock: 4 },
      { name: "250", stock: 4 },
    ],
  },
  {
    id: 1004,
    brandName: "Prada",
    name: "Sandals FW25",
    sku: "1I443OFB07048",
    categories: ["신발", "Sandals"],
    season: "FW25",
    color: "Khaki",
    price: 1352000,
    kreamPrice: 1915000,
    kreamBidCount: 183,
    expectedMargin: 563000,
    marginRate: 29.4,
    sizes: [
      { name: "240", stock: 4 },
      { name: "250", stock: 4 },
    ],
  },
  {
    id: 1005,
    brandName: "Moncler",
    name: "Top SS25",
    sku: "RY0P5012W079",
    categories: ["의류", "TOP"],
    season: "SS25",
    color: "Navy",
    price: 1748000,
    kreamPrice: 2342000,
    kreamBidCount: 151,
    expectedMargin: 594000,
    marginRate: 25.4,
    sizes: [
      { name: "S", stock: 3 },
      { name: "M", stock: 5 },
    ],
  },
  {
    id: 1006,
    brandName: "Gucci",
    name: "Denim Jacket SS25",
    sku: "875438XDDG107",
    categories: ["의류", "Jacket"],
    season: "SS25",
    color: "Blue",
    price: 2469000,
    kreamPrice: 3336000,
    kreamBidCount: 198,
    expectedMargin: 867000,
    marginRate: 26.0,
    sizes: [
      { name: "S", stock: 2 },
      { name: "M", stock: 3 },
    ],
  },
  {
    id: 1007,
    brandName: "Gucci",
    name: "Wallet FW25",
    sku: "10F523CQ7032",
    categories: ["잡화", "Wallet"],
    season: "FW25",
    color: "Black",
    price: 837000,
    kreamPrice: 1131000,
    kreamBidCount: 160,
    expectedMargin: 294000,
    marginRate: 26.0,
    sizes: [{ name: "ONE SIZE", stock: 8 }],
  },
  {
    id: 1008,
    brandName: "Celine",
    name: "Bomber jacket FW26",
    sku: "871997XNBC106",
    categories: ["의류", "Jacket"],
    season: "FW26",
    color: "Black",
    price: 5458000,
    kreamPrice: 6988000,
    kreamBidCount: 176,
    expectedMargin: 1530000,
    marginRate: 21.9,
    sizes: [
      { name: "S", stock: 2 },
      { name: "M", stock: 2 },
    ],
  },
];

export const HOT_DEAL_PRODUCTS: PathPickPopularProduct[] =
  POPULAR_PRODUCT_SEEDS.map(createPopularProduct);

export const POPULAR_DOWNLOAD_PRODUCTS: PathPickPopularProduct[] = Array.from(
  { length: 16 },
  (_, index) => {
    const seed = POPULAR_PRODUCT_SEEDS[index % POPULAR_PRODUCT_SEEDS.length];
    return createPopularProduct({
      ...seed,
      id: 2000 + index + 1,
      kreamBidCount: seed.kreamBidCount - (index % 7) * 3,
    });
  }
);

export const HOT_DEAL_LINE_SHEETS: PathPickHotDealLineSheet[] = [
  {
    id: 1,
    season: "FW25",
    brands: ["Prada", "Gucci"],
    boutique: "Milano Boutique",
    name: "FW25 Hot Deal Stock 01",
    productCount: 4,
    publishedOn: "2026.06.24",
    orderDeadline: "2026.07.15",
  },
  {
    id: 2,
    season: "SS25",
    brands: ["Gucci", "Prada"],
    boutique: "Roma Boutique",
    name: "SS25 Sandals Deal Sheet",
    productCount: 3,
    publishedOn: "2026.06.22",
    orderDeadline: "2026.07.25",
  },
  {
    id: 3,
    season: "FW26",
    brands: ["Celine", "Maison Margiela", "Moncler"],
    boutique: "Paris Boutique",
    name: "Outer & Knitwear Quick Deal",
    productCount: 5,
    publishedOn: "2026.06.20",
    orderDeadline: "2026.07.30",
  },
];

// ===== 요청(requests) 도메인 상수 =====
// 등록 신청 내역 화면과 요청 상세 모달이 함께 쓴다.

// 처리 상태 필터 탭 — "ALL"만 프런트 값이고, 나머지는 그대로 status__in 값이다.
export type PathPickRequestStatusTab =
  | "ALL"
  | PathPickProductRequestStatusFilter;

export const REQUEST_STATUS_TABS: PathPickRequestStatusTab[] = [
  "ALL",
  "SUCCESS",
  "PROCESSING",
  "FAILED",
  "QUOTA_EXCEEDED",
];

// 탭 라벨은 배지 표기(status_*)와 분리해 둔다 — 배지는 연동/엑셀에 따라 문구가 갈리지만
// ("API 연동 완료" / "파일 생성 완료") 탭은 두 모달에서 같은 한 벌을 쓴다.
export const REQUEST_STATUS_TAB_LABEL_KEY: Record<
  PathPickRequestStatusTab,
  string
> = {
  ALL: "path_pick:sync_download_history.status_filter_all",
  SUCCESS: "path_pick:sync_download_history.status_filter_completed",
  PROCESSING: "path_pick:sync_download_history.status_filter_processing",
  FAILED: "path_pick:sync_download_history.status_filter_failed",
  QUOTA_EXCEEDED:
    "path_pick:sync_download_history.status_filter_quota_exceeded",
};

// 전체는 파라미터를 아예 빼서 서버가 전부 내려주게 한다.
export const toStatusFilterParam = (
  tab: PathPickRequestStatusTab
): PathPickProductRequestStatusFilter[] | undefined =>
  tab === "ALL" ? undefined : [tab];

// 재생성(실패 항목 재신청)은 현재 닫아 둔다 — 실패 사유 대부분을 사용자가 스스로 고칠 수 없어
// 다시 신청해도 같은 결과가 나온다. 되살릴 때 이 플래그만 true로 되돌리면 두 모달(신청 상품 내역 /
// 상품별 처리 내역)의 재생성 버튼·일괄 재생성·재생성 확인 모달이 함께 켜진다.
// 플래그로 끄고 코드는 남겨 둔다 — 주석 처리해 두면 타입 검사에서 빠져 되살릴 때 이미 썩는다.
export const IS_REQUEST_RETRY_ENABLED = false;
